import React, { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/api';
import './CompanyLeadsPage.css';

/*
 * Leads the company's advisor produced.
 *
 * Deliberately not a CRM: no pipeline stages, no assignment controls, no
 * campaigns. The question this answers is "who wants what, and have we called
 * them" — everything else is somebody else's product.
 *
 * There is no transcript here and no link to one. A company gets what the lead
 * is looking for, the properties they engaged with, and their contact details
 * once they ask to visit. The conversation itself stays internal, so the page
 * does not hint at it.
 */
export default function CompanyLeadsPage() {
  const [leads, setLeads] = useState([]);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [l, v] = await Promise.all([
        apiClient.get('/company/leads'),
        apiClient.get('/company/visits'),
      ]);
      setLeads(l.data?.leads || []);
      setVisits(v.data?.visits || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d?.message || 'Could not load your leads.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requested = visits.filter((v) => v.status === 'pending').length;

  return (
    <div className="leads-page">
      <div className="leads-summary">
        <Stat label="Leads" value={leads.length} />
        <Stat label="Visits requested" value={requested} tone={requested ? 'warn' : undefined} />
        <Stat label="Visits total" value={visits.length} />
      </div>

      {error && <div className="leads-error">{error}</div>}

      <div className="leads-card">
        <h2 className="leads-title">Leads</h2>
        <p className="leads-desc">
          Everyone your advisor spoke to — not only those who asked to visit.
        </p>

        {loading ? (
          <div className="leads-empty">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="leads-empty">
            No leads yet. Share your advisor link and they'll appear here.
          </div>
        ) : (
          <div className="leads-scroll">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Lead</th><th>Contact</th><th>Interested in</th><th>Status</th><th>First seen</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.lead_id}>
                    <td className="leads-name">{l.name || <span className="leads-anon">Not given yet</span>}</td>
                    <td>{l.phone || l.email || <span className="leads-anon">—</span>}</td>
                    <td className="leads-num">{l.interested_properties}</td>
                    <td><StatusPill status={l.status} /></td>
                    <td>{formatDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="leads-card">
        <h2 className="leads-title">Visit requests</h2>
        <p className="leads-desc">
          Each one was handed to a salesperson's WhatsApp when it came in.
        </p>

        {visits.length === 0 ? (
          <div className="leads-empty">No visit requests yet.</div>
        ) : (
          <div className="leads-scroll">
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Property</th><th>Contact</th><th>Requested</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v) => (
                  <tr key={v.request_id}>
                    <td className="leads-name">{v.compound_name}</td>
                    <td>
                      {v.contact_name ? `${v.contact_name} · ` : ''}
                      {v.contact_phone || v.contact_email || '—'}
                    </td>
                    <td>{formatDate(v.requested_at)}</td>
                    <td><StatusPill status={v.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`leads-stat${tone ? ` leads-stat-${tone}` : ''}`}>
      <span className="leads-stat-value">{value}</span>
      <span className="leads-stat-label">{label}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const known = ['browsing', 'identified', 'visit_requested', 'closed',
                 'pending', 'accepted', 'completed', 'rejected'];
  const cls = known.includes(status) ? status : 'browsing';
  return <span className={`leads-pill leads-pill-${cls}`}>{label(status)}</span>;
}

function label(status) {
  const words = { visit_requested: 'Visit requested', pending: 'Awaiting contact' };
  if (words[status]) return words[status];
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}
